import { useState } from 'react';
import Navbar from '../../components/feature/Navbar';
import Footer from '../../components/feature/Footer';
import AnimatedSection from '../../components/base/AnimatedSection';
import PageTransition from '../../components/base/PageTransition';

export default function FeaturesPage() {
  const [activeTab, setActiveTab] = useState('all');

  const coreFeatures = [
    {
      icon: 'ri-drag-drop-line',
      title: 'Drag & Drop Builder',
      description: 'Intuitive visual editor that lets you build apps by simply dragging and dropping components. No coding required.',
      color: 'from-cyan-500 to-teal-500'
    },
    {
      icon: 'ri-notification-3-line',
      title: 'Push Notifications',
      description: 'Engage users with targeted push notifications. Schedule campaigns and track delivery rates in real-time.',
      color: 'from-teal-500 to-emerald-500'
    },
    {
      icon: 'ri-bank-card-line',
      title: 'In-App Payments',
      description: 'Accept payments seamlessly with integrated Stripe and PayPal. Support subscriptions, one-time purchases, and more.',
      color: 'from-emerald-500 to-green-500'
    },
    {
      icon: 'ri-line-chart-line',
      title: 'Analytics Dashboard',
      description: 'Track user behavior, engagement metrics, and revenue. Make data-driven decisions with comprehensive insights.',
      color: 'from-cyan-500 to-blue-500'
    },
    {
      icon: 'ri-shield-user-line',
      title: 'User Authentication',
      description: 'Secure login with email, social media, or biometric authentication. Manage user roles and permissions easily.',
      color: 'from-teal-500 to-cyan-500'
    },
    {
      icon: 'ri-plug-line',
      title: 'API Integrations',
      description: 'Connect to thousands of third-party services. Build custom integrations with our powerful REST API.',
      color: 'from-emerald-500 to-teal-500'
    }
  ];

  const aiFeatures = [
    {
      icon: 'ri-magic-line',
      title: 'AI App Generator',
      description: 'Describe your app idea in plain English and watch AI build the foundation automatically with smart layouts and features.',
      color: 'from-purple-500 to-pink-500'
    },
    {
      icon: 'ri-lightbulb-flash-line',
      title: 'Smart Recommendations',
      description: 'Get intelligent suggestions for components, features, and design elements based on your app type and user behavior.',
      color: 'from-pink-500 to-rose-500'
    },
    {
      icon: 'ri-bug-line',
      title: 'Automated Testing',
      description: 'AI-powered bug detection and automated testing ensures your app works flawlessly before launch.',
      color: 'from-violet-500 to-purple-500'
    },
    {
      icon: 'ri-quill-pen-line',
      title: 'AI Content Generator',
      description: 'Generate compelling app descriptions, user guides, and in-app copy with AI-powered content creation tools.',
      color: 'from-fuchsia-500 to-pink-500'
    }
  ];

  const backendServices = [
    {
      icon: 'ri-database-2-line',
      title: 'Cloud Database',
      description: 'Built-in cloud database with visual editor. Create tables, relationships, and queries without writing SQL.',
      features: ['Visual database designer', 'Real-time data sync', 'Automated backups', 'Data migration tools']
    },
    {
      icon: 'ri-refresh-line',
      title: 'Real-time Sync',
      description: 'Keep data synchronized across all devices in real-time. Includes offline mode with automatic sync when online.',
      features: ['Instant data updates', 'Offline functionality', 'Conflict resolution', 'Background sync']
    },
    {
      icon: 'ri-code-box-line',
      title: 'Backend Logic Builder',
      description: 'Create custom backend logic with visual workflows. Handle complex business rules without coding.',
      features: ['Visual workflow editor', 'Custom API endpoints', 'Scheduled tasks', 'Event triggers']
    },
    {
      icon: 'ri-shield-check-line',
      title: 'Data Security',
      description: 'Enterprise-grade security with encryption, automated backups, and compliance tools built-in.',
      features: ['End-to-end encryption', 'GDPR compliance', 'Automated backups', 'Access control']
    }
  ];

  const publishingServices = [
    {
      icon: 'ri-store-3-line',
      title: 'App Store Submission',
      description: 'We handle the entire app store submission process for both iOS and Android, saving you time and hassle.',
      badge: 'Popular'
    },
    {
      icon: 'ri-search-eye-line',
      title: 'App Store Optimization',
      description: 'Built-in ASO tools to optimize your app listing with keyword research, A/B testing, and performance tracking.',
      badge: null
    },
    {
      icon: 'ri-flask-line',
      title: 'Beta Testing',
      description: 'Distribute beta versions to testers via TestFlight and Google Play Beta with built-in feedback collection.',
      badge: null
    },
    {
      icon: 'ri-upload-cloud-line',
      title: 'Update Management',
      description: 'Push updates seamlessly with version control, rollback capabilities, and staged rollout options.',
      badge: null
    }
  ];

  const monetizationTools = [
    {
      icon: 'ri-advertisement-line',
      title: 'In-App Advertising',
      description: 'Integrate AdMob, Facebook Ads, and other ad networks with just a few clicks.',
      revenue: 'Passive Income'
    },
    {
      icon: 'ri-repeat-line',
      title: 'Subscription Management',
      description: 'Built-in subscription system with trial periods, tiered pricing, and automatic billing.',
      revenue: 'Recurring Revenue'
    },
    {
      icon: 'ri-links-line',
      title: 'Affiliate Programs',
      description: 'Create and manage affiliate programs to let others promote your app for commission.',
      revenue: 'Growth Channel'
    },
    {
      icon: 'ri-funds-line',
      title: 'Revenue Analytics',
      description: 'Track all revenue streams with detailed analytics, forecasting, and financial reports.',
      revenue: 'Data-Driven'
    }
  ];

  const integrations = [
    { name: 'Stripe', logo: 'ri-bank-card-2-line', color: 'text-purple-600' },
    { name: 'Firebase', logo: 'ri-fire-line', color: 'text-orange-500' },
    { name: 'Google Maps', logo: 'ri-map-pin-line', color: 'text-red-500' },
    { name: 'Twilio', logo: 'ri-message-3-line', color: 'text-red-600' },
    { name: 'Shopify', logo: 'ri-shopping-bag-3-line', color: 'text-green-600' },
    { name: 'PayPal', logo: 'ri-paypal-line', color: 'text-blue-600' },
    { name: 'Mailchimp', logo: 'ri-mail-send-line', color: 'text-yellow-600' },
    { name: 'AWS', logo: 'ri-cloud-line', color: 'text-orange-600' }
  ];

  const appTypes = [
    {
      category: 'eCommerce',
      title: 'eCommerce Apps',
      description: 'Build online stores with product catalogs, shopping carts, and secure checkout.',
      icon: 'ri-shopping-cart-line',
      examples: 'Fashion stores, Electronics shops, Handmade goods',
      image: 'https://public.readdy.ai/ai/img_res/9e07c0717bbe9db1c4d4572ff1147b7d.jpg'
    },
    {
      category: 'food',
      title: 'Food Delivery',
      description: 'Create restaurant apps with menu management, order tracking, and delivery integration.',
      icon: 'ri-restaurant-line',
      examples: 'Restaurant chains, Cloud kitchens, Food marketplaces',
      image: 'https://readdy.ai/api/search-image?query=clean%20food%20delivery%20mobile%20app%20interface%20with%20restaurant%20menu%20items%20and%20order%20button%20on%20white%20background%2C%20modern%20ui%20design%20with%20appetizing%20food%20photography%20and%20teal%20green%20accents&width=400&height=300&seq=food1&orientation=landscape'
    },
    {
      category: 'booking',
      title: 'Booking & Scheduling',
      description: 'Appointment booking systems with calendar sync, reminders, and payment processing.',
      icon: 'ri-calendar-check-line',
      examples: 'Salons, Medical clinics, Fitness studios',
      image: 'https://readdy.ai/api/search-image?query=professional%20booking%20appointment%20mobile%20app%20interface%20with%20calendar%20view%20and%20time%20slots%20on%20clean%20white%20background%2C%20modern%20scheduling%20ui%20design%20with%20cyan%20and%20teal%20color%20scheme&width=400&height=300&seq=book1&orientation=landscape'
    },
    {
      category: 'social',
      title: 'Social Networking',
      description: 'Build communities with user profiles, feeds, messaging, and content sharing.',
      icon: 'ri-group-line',
      examples: 'Community platforms, Dating apps, Professional networks',
      image: 'https://readdy.ai/api/search-image?query=modern%20social%20networking%20mobile%20app%20interface%20with%20user%20profiles%20and%20activity%20feed%20on%20white%20background%2C%20clean%20ui%20design%20with%20profile%20avatars%20and%20teal%20accent%20colors&width=400&height=300&seq=social1&orientation=landscape'
    },
    {
      category: 'ondemand',
      title: 'On-Demand Services',
      description: 'Connect service providers with customers for instant bookings and real-time tracking.',
      icon: 'ri-service-line',
      examples: 'Home services, Transportation, Freelance marketplaces',
      image: 'https://readdy.ai/api/search-image?query=sleek%20on-demand%20service%20mobile%20app%20interface%20showing%20service%20provider%20cards%20and%20booking%20button%20on%20white%20background%2C%20modern%20ui%20design%20with%20location%20map%20and%20teal%20green%20colors&width=400&height=300&seq=ondem1&orientation=landscape'
    },
    {
      category: 'fitness',
      title: 'Fitness & Wellness',
      description: 'Workout tracking, nutrition planning, and wellness coaching apps with progress analytics.',
      icon: 'ri-heart-pulse-line',
      examples: 'Gyms, Yoga studios, Personal trainers',
      image: 'https://readdy.ai/api/search-image?query=modern%20fitness%20wellness%20mobile%20app%20interface%20with%20workout%20tracking%20and%20progress%20charts%20on%20clean%20white%20background%2C%20professional%20health%20app%20ui%20design%20with%20cyan%20and%20teal%20accent%20colors&width=400&height=300&seq=fit1&orientation=landscape'
    }
  ];

  const competitors = [
    {
      name: 'Your App Wizard',
      logo: 'ri-rocket-line',
      features: {
        dragDrop: true,
        nativeApps: true,
        customCode: true,
        whiteLabel: true,
        apiAccess: true,
        support: '24/7',
        price: '$49/mo'
      },
      highlight: true
    },
    {
      name: 'AppGyver',
      logo: 'ri-apps-line',
      features: {
        dragDrop: true,
        nativeApps: true,
        customCode: false,
        whiteLabel: false,
        apiAccess: true,
        support: 'Community',
        price: 'Free'
      }
    },
    {
      name: 'Adalo',
      logo: 'ri-layout-grid-line',
      features: {
        dragDrop: true,
        nativeApps: true,
        customCode: false,
        whiteLabel: true,
        apiAccess: false,
        support: 'Email',
        price: '$50/mo'
      }
    },
    {
      name: 'Glide',
      logo: 'ri-smartphone-line',
      features: {
        dragDrop: true,
        nativeApps: false,
        customCode: false,
        whiteLabel: false,
        apiAccess: true,
        support: 'Email',
        price: '$32/mo'
      }
    }
  ];

  const advancedTools = [
    {
      icon: 'ri-magic-line',
      title: 'AI-Powered Design Suggestions',
      description: 'Get intelligent recommendations for layouts, colors, and components based on your app type and industry best practices.',
      benefits: ['Smart layout optimization', 'Color palette suggestions', 'Component recommendations', 'UX improvements']
    },
    {
      icon: 'ri-git-branch-line',
      title: 'Version Control',
      description: 'Track every change with built-in version control. Roll back to previous versions and manage multiple app variants effortlessly.',
      benefits: ['Unlimited version history', 'One-click rollback', 'Branch management', 'Change comparison']
    },
    {
      icon: 'ri-team-line',
      title: 'Team Collaboration',
      description: 'Work together seamlessly with real-time collaboration tools. Assign roles, leave comments, and manage permissions.',
      benefits: ['Real-time editing', 'Role-based access', 'Comment threads', 'Activity tracking']
    },
    {
      icon: 'ri-palette-line',
      title: 'White-Label Options',
      description: 'Remove our branding and add yours. Perfect for agencies and businesses building apps for clients.',
      benefits: ['Custom branding', 'Your domain', 'Custom splash screens', 'Branded emails']
    }
  ];

  const professionalServices = [
    {
      icon: 'ri-user-star-line',
      title: 'Dedicated Account Manager',
      description: 'Get a dedicated account manager for personalized support and strategic guidance.',
      tier: 'Enterprise'
    },
    {
      icon: 'ri-code-s-slash-line',
      title: 'Custom Development',
      description: 'Need something unique? Our team can build custom features tailored to your needs.',
      tier: 'All Plans'
    },
    {
      icon: 'ri-exchange-line',
      title: 'App Migration Service',
      description: 'Migrating from another platform? We handle the entire migration process for you.',
      tier: 'Pro & Enterprise'
    },
    {
      icon: 'ri-customer-service-2-line',
      title: 'Technical Consultation',
      description: 'Get expert advice with dedicated consultation hours to optimize your app strategy.',
      tier: 'Pro & Enterprise'
    }
  ];

  const performanceTools = [
    {
      icon: 'ri-speed-line',
      title: 'CDN Integration',
      description: 'Global content delivery network ensures fast loading times for users worldwide.',
      metric: '99.9% Uptime'
    },
    {
      icon: 'ri-image-2-line',
      title: 'Image Optimization',
      description: 'Automatic image compression and format conversion for optimal performance.',
      metric: '70% Faster'
    },
    {
      icon: 'ri-dashboard-line',
      title: 'Performance Monitoring',
      description: 'Real-time monitoring with alerts for crashes, slow queries, and performance issues.',
      metric: 'Real-time'
    },
    {
      icon: 'ri-test-tube-line',
      title: 'Load Testing',
      description: 'Simulate thousands of users to ensure your app can handle peak traffic.',
      metric: 'Stress Test'
    }
  ];

  const marketingTools = [
    {
      icon: 'ri-link-m',
      title: 'Deep Linking',
      description: 'Create deep links to specific app content for better user acquisition and engagement.',
    },
    {
      icon: 'ri-gift-line',
      title: 'Referral Program Builder',
      description: 'Built-in referral system to turn users into advocates with rewards and tracking.',
    },
    {
      icon: 'ri-a-b',
      title: 'A/B Testing Framework',
      description: 'Test different features, designs, and flows to optimize conversion rates.',
    },
    {
      icon: 'ri-mail-line',
      title: 'Email Marketing',
      description: 'Integrated email campaigns with templates, automation, and analytics.',
    }
  ];

  const filteredAppTypes = activeTab === 'all' ? appTypes : appTypes.filter(app => app.category === activeTab);

  return (
    <PageTransition>
      <div className="min-h-screen bg-white">
        <Navbar />
        
        {/* Hero Section */}
        <section className="pt-32 pb-20 px-6 bg-gradient-to-br from-cyan-50 via-teal-50 to-white relative overflow-hidden">
          <div className="absolute inset-0 opacity-10">
            <div className="absolute top-20 left-10 w-72 h-72 bg-cyan-500 rounded-full blur-3xl"></div>
            <div className="absolute bottom-20 right-10 w-96 h-96 bg-teal-500 rounded-full blur-3xl"></div>
          </div>
          <div className="max-w-7xl mx-auto text-center relative z-10">
            <AnimatedSection>
              <h1 className="text-5xl md:text-6xl font-bold text-gray-900 mb-6">
                Everything You Need to Build
                <span className="block mt-2 bg-gradient-to-r from-cyan-600 to-teal-600 bg-clip-text text-transparent">
                  Powerful Apps
                </span>
              </h1>
              <p className="text-xl text-gray-600 max-w-3xl mx-auto mb-10">
                From drag-and-drop builders to advanced integrations, we provide all the tools and services you need to create professional mobile apps without writing a single line of code.
              </p>
              <div className="flex flex-wrap gap-4 justify-center">
                <a href="/get-started" className="px-8 py-4 bg-gradient-to-r from-cyan-500 to-teal-500 text-white rounded-lg hover:from-cyan-600 hover:to-teal-600 transition-all duration-300 whitespace-nowrap cursor-pointer font-semibold shadow-lg shadow-cyan-500/30 hover:scale-105 hover:shadow-xl">
                  Start Building Free
                </a>
                <a href="/pricing" className="px-8 py-4 bg-white text-gray-900 rounded-lg hover:bg-gray-50 transition-all duration-300 whitespace-nowrap cursor-pointer font-semibold border-2 border-gray-200 hover:border-cyan-500 hover:scale-105">
                  View Pricing
                </a>
              </div>
            </AnimatedSection>
          </div>
        </section>

        {/* Core Features */}
        <section className="py-20 px-6 bg-white">
          <div className="max-w-7xl mx-auto">
            <AnimatedSection>
              <div className="text-center mb-16">
                <h2 className="text-4xl font-bold text-gray-900 mb-4">Core Features</h2>
                <p className="text-lg text-gray-600 max-w-2xl mx-auto">
                  Powerful tools designed to help you build, launch, and scale your mobile apps with ease.
                </p>
              </div>
            </AnimatedSection>
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
              {coreFeatures.map((feature, index) => (
                <AnimatedSection key={index} delay={index * 100}>
                  <div className="group p-8 rounded-2xl bg-gradient-to-br from-gray-50 to-white border border-gray-100 hover:border-cyan-200 hover:shadow-xl transition-all duration-300 hover:-translate-y-1">
                    <div className={`w-16 h-16 flex items-center justify-center rounded-xl bg-gradient-to-br ${feature.color} mb-6 group-hover:scale-110 transition-transform duration-300`}>
                      <i className={`${feature.icon} text-3xl text-white`}></i>
                    </div>
                    <h3 className="text-xl font-bold text-gray-900 mb-3">{feature.title}</h3>
                    <p className="text-gray-600 leading-relaxed">{feature.description}</p>
                  </div>
                </AnimatedSection>
              ))}
            </div>
          </div>
        </section>

        {/* AI-Powered Features */}
        <section className="py-20 px-6 bg-gradient-to-br from-purple-50 via-pink-50 to-white">
          <div className="max-w-7xl mx-auto">
            <AnimatedSection>
              <div className="text-center mb-16">
                <div className="inline-flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-purple-500 to-pink-500 text-white rounded-full text-sm font-semibold mb-4">
                  <i className="ri-sparkling-line"></i>
                  <span>AI-Powered</span>
                </div>
                <h2 className="text-4xl font-bold text-gray-900 mb-4">Build Smarter with AI</h2>
                <p className="text-lg text-gray-600 max-w-2xl mx-auto">
                  Leverage artificial intelligence to accelerate development, improve quality, and create better user experiences.
                </p>
              </div>
            </AnimatedSection>
            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
              {aiFeatures.map((feature, index) => (
                <AnimatedSection key={index} delay={index * 100}>
                  <div className="group p-6 rounded-2xl bg-white border border-purple-100 hover:border-purple-300 hover:shadow-xl transition-all duration-300 hover:-translate-y-1">
                    <div className={`w-14 h-14 flex items-center justify-center rounded-xl bg-gradient-to-br ${feature.color} mb-4 group-hover:scale-110 transition-transform duration-300`}>
                      <i className={`${feature.icon} text-2xl text-white`}></i>
                    </div>
                    <h3 className="text-lg font-bold text-gray-900 mb-2">{feature.title}</h3>
                    <p className="text-sm text-gray-600 leading-relaxed">{feature.description}</p>
                  </div>
                </AnimatedSection>
              ))}
            </div>
          </div>
        </section>

        {/* Backend Services */}
        <section className="py-20 px-6 bg-white">
          <div className="max-w-7xl mx-auto">
            <AnimatedSection>
              <div className="text-center mb-16">
                <h2 className="text-4xl font-bold text-gray-900 mb-4">Backend & Database Services</h2>
                <p className="text-lg text-gray-600 max-w-2xl mx-auto">
                  Powerful backend infrastructure without the complexity. Focus on building features, not managing servers.
                </p>
              </div>
            </AnimatedSection>
            <div className="grid md:grid-cols-2 gap-8">
              {backendServices.map((service, index) => (
                <AnimatedSection key={index} delay={index * 100}>
                  <div className="group p-8 rounded-2xl bg-gradient-to-br from-gray-50 to-white border border-gray-200 hover:border-cyan-300 hover:shadow-xl transition-all duration-300">
                    <div className="flex items-start gap-6">
                      <div className="w-14 h-14 flex items-center justify-center rounded-xl bg-gradient-to-br from-cyan-500 to-teal-500 flex-shrink-0 group-hover:scale-110 transition-transform duration-300">
                        <i className={`${service.icon} text-2xl text-white`}></i>
                      </div>
                      <div className="flex-1">
                        <h3 className="text-xl font-bold text-gray-900 mb-3">{service.title}</h3>
                        <p className="text-gray-600 mb-4 leading-relaxed">{service.description}</p>
                        <ul className="space-y-2">
                          {service.features.map((feature, idx) => (
                            <li key={idx} className="flex items-center gap-2 text-sm text-gray-700">
                              <i className="ri-check-line text-cyan-500"></i>
                              <span>{feature}</span>
                            </li>
                          ))}
                        </ul>
                      </div>
                    </div>
                  </div>
                </AnimatedSection>
              ))}
            </div>
          </div>
        </section>

        {/* Publishing Services */}
        <section className="py-20 px-6 bg-gradient-to-br from-gray-50 to-white">
          <div className="max-w-7xl mx-auto">
            <AnimatedSection>
              <div className="text-center mb-16">
                <h2 className="text-4xl font-bold text-gray-900 mb-4">Publishing & Distribution</h2>
                <p className="text-lg text-gray-600 max-w-2xl mx-auto">
                  Get your app into users' hands faster with our comprehensive publishing and distribution services.
                </p>
              </div>
            </AnimatedSection>
            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
              {publishingServices.map((service, index) => (
                <AnimatedSection key={index} delay={index * 100}>
                  <div className="group p-6 rounded-2xl bg-white border border-gray-200 hover:border-cyan-300 hover:shadow-xl transition-all duration-300 hover:-translate-y-1 relative">
                    {service.badge && (
                      <div className="absolute -top-3 -right-3 px-3 py-1 bg-gradient-to-r from-cyan-500 to-teal-500 text-white text-xs font-bold rounded-full shadow-lg">
                        {service.badge}
                      </div>
                    )}
                    <div className="w-12 h-12 flex items-center justify-center rounded-lg bg-gradient-to-br from-cyan-500 to-teal-500 mb-4 group-hover:scale-110 transition-transform duration-300">
                      <i className={`${service.icon} text-xl text-white`}></i>
                    </div>
                    <h3 className="text-lg font-bold text-gray-900 mb-2">{service.title}</h3>
                    <p className="text-sm text-gray-600 leading-relaxed">{service.description}</p>
                  </div>
                </AnimatedSection>
              ))}
            </div>
          </div>
        </section>

        {/* Monetization Tools */}
        <section className="py-20 px-6 bg-white">
          <div className="max-w-7xl mx-auto">
            <AnimatedSection>
              <div className="text-center mb-16">
                <h2 className="text-4xl font-bold text-gray-900 mb-4">Monetization Tools</h2>
                <p className="text-lg text-gray-600 max-w-2xl mx-auto">
                  Turn your app into a revenue-generating business with multiple monetization strategies.
                </p>
              </div>
            </AnimatedSection>
            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
              {monetizationTools.map((tool, index) => (
                <AnimatedSection key={index} delay={index * 100}>
                  <div className="group p-6 rounded-2xl bg-gradient-to-br from-green-50 to-emerald-50 border border-green-100 hover:border-green-300 hover:shadow-xl transition-all duration-300 hover:-translate-y-1">
                    <div className="w-12 h-12 flex items-center justify-center rounded-lg bg-gradient-to-br from-green-500 to-emerald-500 mb-4 group-hover:scale-110 transition-transform duration-300">
                      <i className={`${tool.icon} text-xl text-white`}></i>
                    </div>
                    <div className="inline-block px-3 py-1 bg-green-100 text-green-700 text-xs font-semibold rounded-full mb-3">
                      {tool.revenue}
                    </div>
                    <h3 className="text-lg font-bold text-gray-900 mb-2">{tool.title}</h3>
                    <p className="text-sm text-gray-600 leading-relaxed">{tool.description}</p>
                  </div>
                </AnimatedSection>
              ))}
            </div>
          </div>
        </section>

        {/* Integrations */}
        <section className="py-20 px-6 bg-gradient-to-br from-gray-50 to-white">
          <div className="max-w-7xl mx-auto">
            <AnimatedSection>
              <div className="text-center mb-16">
                <h2 className="text-4xl font-bold text-gray-900 mb-4">Seamless Integrations</h2>
                <p className="text-lg text-gray-600 max-w-2xl mx-auto">
                  Connect with the tools you already use. Our platform integrates with leading services to extend your app's capabilities.
                </p>
              </div>
            </AnimatedSection>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
              {integrations.map((integration, index) => (
                <AnimatedSection key={index} delay={index * 50}>
                  <div className="group p-8 rounded-xl bg-white border border-gray-200 hover:border-cyan-300 hover:shadow-lg transition-all duration-300 flex flex-col items-center justify-center hover:-translate-y-1 cursor-pointer">
                    <i className={`${integration.logo} text-5xl ${integration.color} mb-4 group-hover:scale-110 transition-transform duration-300`}></i>
                    <span className="text-sm font-semibold text-gray-700">{integration.name}</span>
                  </div>
                </AnimatedSection>
              ))}
            </div>
          </div>
        </section>

        {/* App Types */}
        <section className="py-20 px-6 bg-white">
          <div className="max-w-7xl mx-auto">
            <AnimatedSection>
              <div className="text-center mb-12">
                <h2 className="text-4xl font-bold text-gray-900 mb-4">App Types You Can Build</h2>
                <p className="text-lg text-gray-600 max-w-2xl mx-auto mb-8">
                  From eCommerce to social networking, build any type of app for any industry.
                </p>
                <div className="flex flex-wrap gap-3 justify-center">
                  {[
                    { id: 'all', label: 'All Types' },
                    { id: 'eCommerce', label: 'eCommerce' },
                    { id: 'food', label: 'Food & Delivery' },
                    { id: 'booking', label: 'Booking' },
                    { id: 'social', label: 'Social' },
                    { id: 'ondemand', label: 'On-Demand' },
                    { id: 'fitness', label: 'Fitness' }
                  ].map(tab => (
                    <button
                      key={tab.id}
                      onClick={() => setActiveTab(tab.id)}
                      className={`px-6 py-2.5 rounded-full font-medium text-sm transition-all duration-300 whitespace-nowrap cursor-pointer ${
                        activeTab === tab.id
                          ? 'bg-gradient-to-r from-cyan-500 to-teal-500 text-white shadow-lg shadow-cyan-500/30'
                          : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                      }`}
                    >
                      {tab.label}
                    </button>
                  ))}
                </div>
              </div>
            </AnimatedSection>
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
              {filteredAppTypes.map((app, index) => (
                <AnimatedSection key={index} delay={index * 100}>
                  <div className="group rounded-2xl overflow-hidden bg-white border border-gray-200 hover:border-cyan-300 hover:shadow-xl transition-all duration-300 hover:-translate-y-1">
                    <div className="w-full h-48 overflow-hidden bg-gray-100">
                      <img 
                        src={app.image} 
                        alt={app.title}
                        className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                      />
                    </div>
                    <div className="p-6">
                      <div className="w-12 h-12 flex items-center justify-center rounded-lg bg-gradient-to-br from-cyan-500 to-teal-500 mb-4">
                        <i className={`${app.icon} text-2xl text-white`}></i>
                      </div>
                      <h3 className="text-xl font-bold text-gray-900 mb-2">{app.title}</h3>
                      <p className="text-gray-600 mb-4 leading-relaxed">{app.description}</p>
                      <div className="pt-4 border-t border-gray-100">
                        <p className="text-sm text-gray-500 mb-1 font-medium">Perfect for:</p>
                        <p className="text-sm text-gray-700">{app.examples}</p>
                      </div>
                    </div>
                  </div>
                </AnimatedSection>
              ))}
            </div>
          </div>
        </section>

        {/* Performance & Optimization */}
        <section className="py-20 px-6 bg-gradient-to-br from-gray-50 to-white">
          <div className="max-w-7xl mx-auto">
            <AnimatedSection>
              <div className="text-center mb-16">
                <h2 className="text-4xl font-bold text-gray-900 mb-4">Performance & Optimization</h2>
                <p className="text-lg text-gray-600 max-w-2xl mx-auto">
                  Deliver lightning-fast experiences with built-in performance optimization and monitoring tools.
                </p>
              </div>
            </AnimatedSection>
            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
              {performanceTools.map((tool, index) => (
                <AnimatedSection key={index} delay={index * 100}>
                  <div className="group p-6 rounded-2xl bg-white border border-gray-200 hover:border-cyan-300 hover:shadow-xl transition-all duration-300 hover:-translate-y-1">
                    <div className="w-12 h-12 flex items-center justify-center rounded-lg bg-gradient-to-br from-cyan-500 to-teal-500 mb-4 group-hover:scale-110 transition-transform duration-300">
                      <i className={`${tool.icon} text-xl text-white`}></i>
                    </div>
                    <div className="inline-block px-3 py-1 bg-cyan-100 text-cyan-700 text-xs font-semibold rounded-full mb-3">
                      {tool.metric}
                    </div>
                    <h3 className="text-lg font-bold text-gray-900 mb-2">{tool.title}</h3>
                    <p className="text-sm text-gray-600 leading-relaxed">{tool.description}</p>
                  </div>
                </AnimatedSection>
              ))}
            </div>
          </div>
        </section>

        {/* Marketing Tools */}
        <section className="py-20 px-6 bg-white">
          <div className="max-w-7xl mx-auto">
            <AnimatedSection>
              <div className="text-center mb-16">
                <h2 className="text-4xl font-bold text-gray-900 mb-4">Marketing & Growth Tools</h2>
                <p className="text-lg text-gray-600 max-w-2xl mx-auto">
                  Acquire, engage, and retain users with powerful marketing tools built right into the platform.
                </p>
              </div>
            </AnimatedSection>
            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
              {marketingTools.map((tool, index) => (
                <AnimatedSection key={index} delay={index * 100}>
                  <div className="group p-6 rounded-2xl bg-gradient-to-br from-orange-50 to-amber-50 border border-orange-100 hover:border-orange-300 hover:shadow-xl transition-all duration-300 hover:-translate-y-1">
                    <div className="w-12 h-12 flex items-center justify-center rounded-lg bg-gradient-to-br from-orange-500 to-amber-500 mb-4 group-hover:scale-110 transition-transform duration-300">
                      <i className={`${tool.icon} text-xl text-white`}></i>
                    </div>
                    <h3 className="text-lg font-bold text-gray-900 mb-2">{tool.title}</h3>
                    <p className="text-sm text-gray-600 leading-relaxed">{tool.description}</p>
                  </div>
                </AnimatedSection>
              ))}
            </div>
          </div>
        </section>

        {/* Professional Services */}
        <section className="py-20 px-6 bg-gradient-to-br from-gray-50 to-white">
          <div className="max-w-7xl mx-auto">
            <AnimatedSection>
              <div className="text-center mb-16">
                <h2 className="text-4xl font-bold text-gray-900 mb-4">Professional Services</h2>
                <p className="text-lg text-gray-600 max-w-2xl mx-auto">
                  Get expert help when you need it. Our team is here to support your success every step of the way.
                </p>
              </div>
            </AnimatedSection>
            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
              {professionalServices.map((service, index) => (
                <AnimatedSection key={index} delay={index * 100}>
                  <div className="group p-6 rounded-2xl bg-white border border-gray-200 hover:border-cyan-300 hover:shadow-xl transition-all duration-300 hover:-translate-y-1">
                    <div className="w-12 h-12 flex items-center justify-center rounded-lg bg-gradient-to-br from-cyan-500 to-teal-500 mb-4 group-hover:scale-110 transition-transform duration-300">
                      <i className={`${service.icon} text-xl text-white`}></i>
                    </div>
                    <div className="inline-block px-3 py-1 bg-gray-100 text-gray-700 text-xs font-semibold rounded-full mb-3">
                      {service.tier}
                    </div>
                    <h3 className="text-lg font-bold text-gray-900 mb-2">{service.title}</h3>
                    <p className="text-sm text-gray-600 leading-relaxed">{service.description}</p>
                  </div>
                </AnimatedSection>
              ))}
            </div>
          </div>
        </section>

        {/* Comparison Table */}
        <section className="py-20 px-6 bg-white">
          <div className="max-w-7xl mx-auto">
            <AnimatedSection>
              <div className="text-center mb-16">
                <h2 className="text-4xl font-bold text-gray-900 mb-4">How We Compare</h2>
                <p className="text-lg text-gray-600 max-w-2xl mx-auto">
                  See why Your App Wizard is the best choice for building professional mobile apps.
                </p>
              </div>
            </AnimatedSection>
            <div className="overflow-x-auto">
              <table className="w-full bg-white rounded-2xl overflow-hidden shadow-lg">
                <thead>
                  <tr className="bg-gradient-to-r from-gray-50 to-gray-100">
                    <th className="px-6 py-5 text-left text-sm font-bold text-gray-900">Feature</th>
                    {competitors.map((comp, index) => (
                      <th key={index} className={`px-6 py-5 text-center text-sm font-bold ${comp.highlight ? 'bg-gradient-to-br from-cyan-500 to-teal-500 text-white' : 'text-gray-900'}`}>
                        <div className="flex flex-col items-center gap-2">
                          <i className={`${comp.logo} text-2xl`}></i>
                          <span>{comp.name}</span>
                          {comp.highlight && (
                            <span className="px-3 py-1 bg-white/20 rounded-full text-xs font-semibold">Best Value</span>
                          )}
                        </div>
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {[
                    { label: 'Drag & Drop Builder', key: 'dragDrop' },
                    { label: 'Native iOS & Android', key: 'nativeApps' },
                    { label: 'Custom Code Support', key: 'customCode' },
                    { label: 'White-Label Options', key: 'whiteLabel' },
                    { label: 'Full API Access', key: 'apiAccess' },
                    { label: 'Customer Support', key: 'support' },
                    { label: 'Starting Price', key: 'price' }
                  ].map((row, rowIndex) => (
                    <tr key={rowIndex} className={rowIndex % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
                      <td className="px-6 py-4 text-sm font-semibold text-gray-900">{row.label}</td>
                      {competitors.map((comp, compIndex) => (
                        <td key={compIndex} className={`px-6 py-4 text-center ${comp.highlight ? 'bg-cyan-50' : ''}`}>
                          {typeof comp.features[row.key as keyof typeof comp.features] === 'boolean' ? (
                            comp.features[row.key as keyof typeof comp.features] ? (
                              <i className="ri-check-line text-2xl text-green-500"></i>
                            ) : (
                              <i className="ri-close-line text-2xl text-gray-300"></i>
                            )
                          ) : (
                            <span className={`text-sm font-semibold ${comp.highlight ? 'text-cyan-600' : 'text-gray-700'}`}>
                              {comp.features[row.key as keyof typeof comp.features] as string}
                            </span>
                          )}
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </section>

        {/* Advanced Tools */}
        <section className="py-20 px-6 bg-gradient-to-br from-gray-50 to-white">
          <div className="max-w-7xl mx-auto">
            <AnimatedSection>
              <div className="text-center mb-16">
                <h2 className="text-4xl font-bold text-gray-900 mb-4">Advanced Tools</h2>
                <p className="text-lg text-gray-600 max-w-2xl mx-auto">
                  Take your app development to the next level with our professional-grade tools and features.
                </p>
              </div>
            </AnimatedSection>
            <div className="grid md:grid-cols-2 gap-8">
              {advancedTools.map((tool, index) => (
                <AnimatedSection key={index} delay={index * 100}>
                  <div className="group p-8 rounded-2xl bg-gradient-to-br from-white to-gray-50 border border-gray-200 hover:border-cyan-300 hover:shadow-xl transition-all duration-300">
                    <div className="flex items-start gap-6">
                      <div className="w-14 h-14 flex items-center justify-center rounded-xl bg-gradient-to-br from-cyan-500 to-teal-500 flex-shrink-0 group-hover:scale-110 transition-transform duration-300">
                        <i className={`${tool.icon} text-2xl text-white`}></i>
                      </div>
                      <div className="flex-1">
                        <h3 className="text-xl font-bold text-gray-900 mb-3">{tool.title}</h3>
                        <p className="text-gray-600 mb-4 leading-relaxed">{tool.description}</p>
                        <ul className="space-y-2">
                          {tool.benefits.map((benefit, idx) => (
                            <li key={idx} className="flex items-center gap-2 text-sm text-gray-700">
                              <i className="ri-check-line text-cyan-500"></i>
                              <span>{benefit}</span>
                            </li>
                          ))}
                        </ul>
                      </div>
                    </div>
                  </div>
                </AnimatedSection>
              ))}
            </div>
          </div>
        </section>

        {/* Final CTA */}
        <section className="py-20 px-6 bg-gradient-to-br from-cyan-500 via-teal-500 to-emerald-500 relative overflow-hidden">
          <div className="absolute inset-0 opacity-10">
            <div className="absolute top-0 left-0 w-96 h-96 bg-white rounded-full blur-3xl"></div>
            <div className="absolute bottom-0 right-0 w-96 h-96 bg-white rounded-full blur-3xl"></div>
          </div>
          <div className="max-w-4xl mx-auto text-center relative z-10">
            <AnimatedSection>
              <h2 className="text-4xl md:text-5xl font-bold text-white mb-6">
                Ready to Build Your Dream App?
              </h2>
              <p className="text-xl text-white/90 mb-10 max-w-2xl mx-auto">
                Join thousands of creators who are building amazing apps with Your App Wizard. Start your free trial today—no credit card required.
              </p>
              <div className="flex flex-wrap gap-4 justify-center">
                <a href="/get-started" className="px-8 py-4 bg-white text-cyan-600 rounded-lg hover:bg-gray-50 transition-all duration-300 whitespace-nowrap cursor-pointer font-bold shadow-xl hover:scale-105 hover:shadow-2xl">
                  Start Building Free
                </a>
                <a href="/pricing" className="px-8 py-4 bg-white/10 backdrop-blur-sm text-white rounded-lg hover:bg-white/20 transition-all duration-300 whitespace-nowrap cursor-pointer font-bold border-2 border-white/30 hover:border-white/50 hover:scale-105">
                  View All Plans
                </a>
              </div>
              <div className="flex flex-wrap gap-8 justify-center mt-12 text-white/90">
                <div className="flex items-center gap-2">
                  <i className="ri-check-double-line text-xl"></i>
                  <span className="text-sm font-medium">Free 14-day trial</span>
                </div>
                <div className="flex items-center gap-2">
                  <i className="ri-check-double-line text-xl"></i>
                  <span className="text-sm font-medium">No credit card required</span>
                </div>
                <div className="flex items-center gap-2">
                  <i className="ri-check-double-line text-xl"></i>
                  <span className="text-sm font-medium">Cancel anytime</span>
                </div>
              </div>
            </AnimatedSection>
          </div>
        </section>

        <Footer />
      </div>
    </PageTransition>
  );
}